import { useEffect, useState } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import { Avatar, Spinner, Text } from "../../theme/primitives";
import { getProfiles } from "../../api/client";
import type { UserProfile } from "../../api/types";
import { SidePanel } from "./SidePanel";

interface ProfilePanelProps {
  client: MatrixClient;
  userId: string;
  fallbackName: string;
  avatarUrl: string | null;
  status?: { text: string; online: boolean };
  onClose: () => void;
}

/** Contact-info drawer: avatar, name, company, email, user id, presence. */
export function ProfilePanel({ client, userId, fallbackName, avatarUrl, status, onClose }: ProfilePanelProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const token = client.getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }
    getProfiles([userId], token, controller.signal)
      .then((rows) => setProfile(rows.find((r) => r.matrixUserId === userId) ?? null))
      .catch(() => undefined)
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [client, userId]);

  const name = profile?.displayName || fallbackName;
  const meta = profile?.meta ?? undefined;
  // Avatar can be supplied directly via meta.avatar; else the Matrix avatar.
  const avatarSrc = meta?.avatar?.value || avatarUrl;
  const company = meta?.company?.value ?? profile?.company ?? undefined;
  // Loop every meta field (except avatar, which is the image itself).
  const metaFields = meta
    ? Object.values(meta).filter((f) => f.code !== "avatar" && f.value)
    : [];

  return (
    <SidePanel title="Contact info" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "var(--space-xl)" }}>
        <Avatar name={name} src={avatarSrc} size={96} />
        <Text weight="bold" size="xl">{name}</Text>
        {company && (
          <Text size="sm" muted>{company}</Text>
        )}
        {status && (
          <Text size="sm" style={{ color: status.online ? "var(--color-online)" : "var(--color-text-muted)" }}>
            {status.text}
          </Text>
        )}
      </div>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-lg)" }}>
          <Spinner size={20} />
        </div>
      ) : (
        <div style={{ borderTop: "1px solid var(--color-border)" }}>
          {metaFields.map((f) => (
            <Field key={f.code} label={f.displayName} value={f.value} />
          ))}
          <Field label="Email" value={profile?.email ?? "—"} />
          <Field label="User ID" value={userId} />
        </div>
      )}
    </SidePanel>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: "var(--space-md) var(--space-lg)", borderBottom: "1px solid var(--color-border)" }}>
      <Text size="sm" muted>{label}</Text>
      <div style={{ marginTop: 2 }}>
        <Text style={{ wordBreak: "break-word" }}>{value}</Text>
      </div>
    </div>
  );
}
